import React, { useState } from 'react';
import styled from 'styled-components';
import { useKidsChildren } from '../hooks/useKidsChildren';
import { useKidsChurchChatUsers } from '../hooks/useKidsChurchChatUsers';
import { usePosts } from '../hooks/usePosts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasKidsChurchStaffRole } from '../utils/roles';
import { getKidsChurchClass, KIDS_CHURCH_CLASSES, type KidsChurchClassId } from '../utils/kidsChurchClass';

// Re-usable styled components ----------------------------------------------
const Page = styled.div`
  background: var(--surfaceVariant);
  padding: 2rem 2.5rem;
  min-height: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`;

const TitleIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--surfaceVariant);
  color: var(--primary);
  display: grid;
  place-items: center;
  font-weight: 700;
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.6rem;
  color: var(--onSurface);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--onSurfaceVariant);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const HeaderButton = styled.button<{ $variant?: 'primary' | 'outline' }>`
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $variant }) => ($variant === 'primary' ? 'var(--onPrimary)' : 'var(--onSurface)')};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(15, 23, 42, 0.1);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  margin-bottom: 2rem;
`;

const Card = styled.div<{ $clickable?: boolean }>`
  padding: 1.5rem;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--outline);
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? 'translateY(-2px)' : 'none')};
    box-shadow: 0 14px 28px rgba(17, 24, 39, 0.08);
  }
`;

const MetricTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const MetricIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--surfaceVariant);
  color: var(--primary);
  display: grid;
  place-items: center;
  font-size: 1.1rem;
  font-weight: 700;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MetricLabel = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
  font-weight: 600;
`;

const MetricFootnote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
`;

const LiveBadge = styled.span`
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  color: var(--primary);
  font-size: 0.7rem;
  font-weight: 700;
`;

// Button styling similar to dashboard image
const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  font-size: 0.8rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  color: var(--onPrimary);
  background: ${({ $variant }) =>
    $variant === 'danger'
      ? 'var(--error)'
      : $variant === 'secondary'
      ? 'var(--onSurfaceVariant)'
      : 'var(--primary)'};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RowActionButton = styled(ActionButton)`
  font-size: 0.75rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
`;

// Card wrapper for the table to provide consistent card styling
const TableCard = styled(Card)`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0;
`;

const MetricValue = styled.h2`
  margin: 0;
  font-size: 2.1rem;
  color: var(--onSurface);
`;

const TabsBar = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--outline);
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: 0.55rem 0;
  border: none;
  background: transparent;
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-bottom: 2px solid ${({ $active }) => ($active ? 'var(--primary)' : 'transparent')};
`;

const TabIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: inherit;
`;

const WallCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  margin-bottom: 1rem;
`;

const WallPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
  justify-items: start;
`;

const WallPostCard = styled(WallCard)`
  width: 100%;
`;

const WallFormCard = styled(WallCard)`
  width: min(520px, 100%);
`;

const WallMeta = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.35rem;
`;

const WallTitle = styled.h4`
  margin: 0 0 0.35rem 0;
`;

const WallBody = styled.p`
  margin: 0;
  white-space: pre-wrap;
`;

const WallImagePreview = styled.img`
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--outline);
  margin-top: 0.5rem;
`;

const WallPostImage = styled.img`
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--outline);
  margin-top: 0.75rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const FormRowFull = styled(FormRow)`
  grid-column: 1 / -1;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  min-height: 120px;
  background: var(--surface);
  color: var(--onSurface);
`;

const ErrorText = styled.p`
  color: var(--error);
  margin: 0.25rem 0 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const SectionSubtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  min-width: 260px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.85rem;
  background: transparent;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--surfaceVariant)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
`;

const ClassFilter = styled.select`
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  margin: 0 0;
  border-collapse: collapse;

  th,
  td {
    padding: 1.2rem 1rem;
    text-align: left;
  }

  thead th {
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: 600;
    border-bottom: 2px solid var(--outline);
    text-align: left;
  }

  tbody tr {
    border-bottom: 1px solid var(--outline);

    &:hover {
      background: var(--surfaceVariant);
    }
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const Pagination = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primaryDark)' : 'var(--surfaceVariant)')};
  }
`;

const PageEllipsis = styled.span`
  padding: 0 0.4rem;
  color: var(--onSurfaceVariant);
  font-weight: 600;
`;

const StatusPill = styled.span<{ $status: 'in' | 'out' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $status }) => ($status === 'in' ? 'var(--success)' : 'var(--onSurfaceVariant)')};
  background: ${({ $status }) => ($status === 'in' ? 'var(--successContainer)' : 'var(--outline)')};
`;

const Tag = styled.span<{ $tone?: 'danger' | 'warning' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'danger' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--onSurfaceVariant)'};
  background: ${({ $tone }) =>
    $tone === 'danger' ? 'var(--errorContainer)' : $tone === 'warning' ? 'var(--warningContainer)' : 'var(--outline)'};
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--outline);
  color: var(--onSurface);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 0.75rem;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--onSurfaceVariant);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MessageCountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9999px;
  background: var(--error);
  color: var(--onPrimary);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

const ModalContent = styled.div`
  width: min(640px, 92vw);
  background: var(--surface);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.25rem;
  cursor: pointer;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.25rem;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 180px;
  overflow: auto;
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: var(--onSurface);

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
`;

const ModalValue = styled.p`
  margin: 0.15rem 0 0;
  font-weight: 600;
  color: var(--onSurface);
`;

const ToastRegion = styled.div`
  position: fixed;
  top: 5rem;
  right: 1.25rem;
  z-index: 220;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  pointer-events: none;
`;

const Toast = styled.div<{ $variant: 'success' | 'error' | 'info' }>`
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--onSurface);
  background: ${({ $variant }) =>
    $variant === 'success' ? 'var(--successContainer)' : $variant === 'error' ? 'var(--errorContainer)' : 'var(--surfaceVariant)'};
  border: 1px solid
    ${({ $variant }) => ($variant === 'success' ? 'var(--success)' : $variant === 'error' ? 'var(--error)' : 'var(--accent)')};
  pointer-events: auto;
  max-width: 360px;
`;

const ToastTimestamp = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  margin-top: 0.25rem;
`;

const formatParentPin = (pin?: string | null) => (pin ? pin : 'Not set');

export const KidsChurchPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const { data: chatUsers = [] } = useKidsChurchChatUsers(user?.id || '');
  const isTeacherRoleStaff = hasKidsChurchStaffRole(profile?.roles ?? user?.user_metadata?.roles);

  // Search state. The query uses a debounced value so typing does not
  // replace the page with the full-screen loading state on each keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCheckedInOnly, setShowCheckedInOnly] = useState(false);
  const [classFilter, setClassFilter] = useState<KidsChurchClassId | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;
  const [activeTab, setActiveTab] = useState<'children' | 'wall' | 'messages'>('children');
  const [wallTitle, setWallTitle] = useState('');
  const [wallContent, setWallContent] = useState('');
  const [wallImageUrl, setWallImageUrl] = useState('');
  const [uploadingWallImage, setUploadingWallImage] = useState(false);
  const wallImageInputRef = React.useRef<HTMLInputElement | null>(null);
  const [selectedKidId, setSelectedKidId] = useState<string | null>(null);
  const [householdSearch, setHouseholdSearch] = useState('');
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(null);
  const [householdModalChild, setHouseholdModalChild] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
    timestamp: string;
  } | null>(null);
  const toastTimerRef = React.useRef<number | null>(null);
  const lastLocalActionRef = React.useRef<{
    childId: string;
    type: 'checkin' | 'checkout';
    at: number;
  } | null>(null);
  type OpenCheckin = {
    id: string;
    child_id: string;
    checked_in_by?: string | null;
    check_in_time?: string | null;
  };

  const openCheckinsRef = React.useRef<OpenCheckin[]>([]);
  const hasLoadedOpenCheckinsRef = React.useRef(false);
  const toastEventRef = React.useRef<Map<string, number>>(new Map());

  const showToast = React.useCallback(
    (message: string, variant: 'success' | 'error' | 'info' = 'success', timestamp?: string) => {
      setToast({ message, variant, timestamp: timestamp ?? new Date().toLocaleTimeString() });
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null);
      }, 5000);
    },
    []
  );

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const parentIdsRef = React.useRef<Set<string>>(new Set());

  const getActorLabel = React.useCallback(
    (
      actorId?: string | null,
      parentId?: string | null,
      fallback: 'Staff' | 'Parent' | '—' = 'Staff'
    ) => {
      if (!actorId) {
        if (parentId) return 'Parent';
        return fallback;
      }
      if (parentId && actorId === parentId) return 'Parent';
      if (!parentId && parentIdsRef.current.has(actorId)) return 'Parent';
      return 'Staff';
    },
    []
  );

  const shouldSuppressRealtimeToast = React.useCallback(
    (childId?: string | null, type?: 'checkin' | 'checkout', actorId?: string | null) => {
      if (!childId || !type) return false;
      const last = lastLocalActionRef.current;
      if (!last) return false;
      if (actorId && actorId !== user?.id) return false;
      const isMatch = last.childId === childId && last.type === type && Date.now() - last.at < 5000;
      if (isMatch) lastLocalActionRef.current = null;
      return isMatch;
    },
    [user?.id]
  );

  const hasRecentToast = React.useCallback((checkinId: string, type: 'checkin' | 'checkout') => {
    const key = `${checkinId}:${type}`;
    const last = toastEventRef.current.get(key);
    if (last && Date.now() - last < 5000) return true;
    return false;
  }, []);

  const markToastShown = React.useCallback((checkinId: string, type: 'checkin' | 'checkout') => {
    toastEventRef.current.set(`${checkinId}:${type}`, Date.now());
  }, []);

  // Children list (for table)
  const {
    data: kidsResult,
    isLoading: kidsLoading,
    isFetching: kidsFetching,
    error: kidsError,
  } = useKidsChildren({
    page: currentPage,
    pageSize,
    search: debouncedSearch,
    isStaffUser: isTeacherRoleStaff,
    checkedInOnly: showCheckedInOnly,
    classFilter,
  });
  const kids = React.useMemo(() => kidsResult?.kids ?? [], [kidsResult?.kids]);
  const canViewParentPins = Boolean(kidsResult?.canViewParentPins);
  const childColumnCount = canViewParentPins ? 6 : 5;
  const totalKidsCount = React.useMemo(
    () => kidsResult?.totalCount ?? 0,
    [kidsResult?.totalCount]
  );
  const kidsRef = React.useRef<typeof kids>(kids);

  React.useEffect(() => {
    kidsRef.current = kids;
  }, [kids]);

  React.useEffect(() => {
    const parentIds = new Set(
      (kids ?? [])
        .map((kid) => kid.parent_id)
        .filter((id): id is string => Boolean(id))
    );
    parentIdsRef.current = parentIds;
  }, [kids]);

  const { data: wallPosts = [], isLoading: wallLoading, error: wallError } = usePosts('all', {
    type: 'kids_church',
    status: 'published',
  });

  const { data: staffRecord } = useQuery({
    queryKey: ['kids-church', 'staff', user?.id ?? ''],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kids_church_staff')
        .select('id, role, is_active')
        .eq('user_id', user?.id ?? '')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const isStaff = Boolean(staffRecord?.is_active) || isTeacherRoleStaff;

  const { data: households = [] } = useQuery({
    queryKey: ['kids-church', 'households'],
    enabled: isStaff,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('households')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const filteredHouseholds = React.useMemo(() => {
    const term = householdSearch.trim().toLowerCase();
    if (!term) return [];
    return households.filter((item) => item.name.toLowerCase().includes(term));
  }, [householdSearch, households]);

  // Total children metric
  const { data: totalChildren } = useQuery({
    queryKey: ['kids-church', 'metrics', 'totalChildren'],
    queryFn: async () => {
      const { count } = await supabase
        .from('kids_church_children')
        .select('id', { count: 'exact', head: true });
      return count ?? 0;
    },
  });

  const { data: lastCheckin } = useQuery({
    queryKey: ['kids-church', 'last-checkin', selectedKidId ?? 'none'],
    enabled: Boolean(selectedKidId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kids_church_checkins')
        .select('check_in_time, check_out_time, checked_in_by, checked_out_by')
        .eq('child_id', selectedKidId ?? '')
        .order('check_in_time', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as {
        check_in_time: string | null;
        check_out_time: string | null;
        checked_in_by: string | null;
        checked_out_by: string | null;
      } | null;
    },
  });

  const showCheckinToast = React.useCallback(
    (checkin: OpenCheckin) => {
      if (shouldSuppressRealtimeToast(checkin.child_id, 'checkin', checkin.checked_in_by)) return;
      if (hasRecentToast(checkin.id, 'checkin')) return;
      const kid = kidsRef.current?.find((k) => k.id === checkin.child_id);
      const name =
        `${kid?.first_name ?? ''} ${kid?.last_name ?? ''}`.trim() || kid?.parent_name || 'Child';
      const actorLabel = getActorLabel(checkin.checked_in_by, kid?.parent_id, 'Staff');
      const timeLabel = checkin.check_in_time
        ? new Date(checkin.check_in_time).toLocaleTimeString()
        : undefined;
      showToast(`${name} checked in by ${actorLabel}`, 'info', timeLabel);
      markToastShown(checkin.id, 'checkin');
    },
    [getActorLabel, hasRecentToast, markToastShown, shouldSuppressRealtimeToast, showToast]
  );

  const showCheckoutToastById = React.useCallback(
    async (checkinId: string) => {
      if (hasRecentToast(checkinId, 'checkout')) return;
      const { data, error } = await supabase
        .from('kids_church_checkins')
        .select('id, child_id, checked_out_by, check_out_time')
        .eq('id', checkinId)
        .maybeSingle();
      if (error || !data) {
        console.debug('[KidsChurch][realtime] checkout fetch failed', { checkinId, error });
        return;
      }
      if (shouldSuppressRealtimeToast(data.child_id, 'checkout', data.checked_out_by)) return;
      const kid = kidsRef.current?.find((k) => k.id === data.child_id);
      const name =
        `${kid?.first_name ?? ''} ${kid?.last_name ?? ''}`.trim() || kid?.parent_name || 'Child';
      const actorLabel = getActorLabel(data.checked_out_by, kid?.parent_id, 'Staff');
      const timeLabel = data.check_out_time
        ? new Date(data.check_out_time).toLocaleTimeString()
        : undefined;
      showToast(`${name} checked out by ${actorLabel}`, 'info', timeLabel);
      markToastShown(checkinId, 'checkout');
    },
    [getActorLabel, hasRecentToast, markToastShown, shouldSuppressRealtimeToast, showToast]
  );

  const showCheckoutToastFromRecord = React.useCallback(
    (record: {
      id: string;
      child_id?: string | null;
      checked_out_by?: string | null;
      check_out_time?: string | null;
    }) => {
      if (!record.check_out_time || !record.child_id) return;
      if (shouldSuppressRealtimeToast(record.child_id, 'checkout', record.checked_out_by)) return;
      if (hasRecentToast(record.id, 'checkout')) return;
      const kid = kidsRef.current?.find((k) => k.id === record.child_id);
      const name =
        `${kid?.first_name ?? ''} ${kid?.last_name ?? ''}`.trim() || kid?.parent_name || 'Child';
      const actorLabel = getActorLabel(record.checked_out_by, kid?.parent_id, 'Staff');
      const timeLabel = record.check_out_time
        ? new Date(record.check_out_time).toLocaleTimeString()
        : undefined;
      showToast(`${name} checked out by ${actorLabel}`, 'info', timeLabel);
      markToastShown(record.id, 'checkout');
    },
    [getActorLabel, hasRecentToast, markToastShown, shouldSuppressRealtimeToast, showToast]
  );

  React.useEffect(() => {
    const channel = supabase
      .channel('kids-church-checkins-toast')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'kids_church_checkins' },
        (payload) => {
          console.debug('[KidsChurch][realtime] INSERT', payload);
          const record = payload.new as {
            id: string;
            child_id?: string | null;
            checked_in_by?: string | null;
            check_in_time?: string | null;
          };
          if (!record.child_id) return;
          showCheckinToast({ ...record, child_id: record.child_id });
          queryClient.invalidateQueries({ queryKey: ['kids-church', 'open-checkins'] });
          queryClient.invalidateQueries({ queryKey: ['kids-children'] });
          queryClient.invalidateQueries({ queryKey: ['kids-church', 'last-checkin'] });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'kids_church_checkins' },
        (payload) => {
          console.debug('[KidsChurch][realtime] UPDATE', payload);
          const record = payload.new as {
            id: string;
            child_id?: string | null;
            checked_out_by?: string | null;
            check_out_time?: string | null;
          };
          showCheckoutToastFromRecord(record);
          queryClient.invalidateQueries({ queryKey: ['kids-church', 'open-checkins'] });
          queryClient.invalidateQueries({ queryKey: ['kids-children'] });
          queryClient.invalidateQueries({ queryKey: ['kids-church', 'last-checkin'] });
        }
      )
      .subscribe((status) => {
        console.debug('[KidsChurch][realtime] status', status);
        if (status === 'CHANNEL_ERROR') {
          showToast('Realtime connection error', 'error');
        }
      });

    return () => {
      console.debug('[KidsChurch][realtime] cleanup');
      supabase.removeChannel(channel);
    };
  }, [queryClient, showCheckinToast, showCheckoutToastFromRecord, showToast, user?.id]);

  // Current open check-ins list
  const { data: openCheckins = [] } = useQuery<OpenCheckin[]>({
    queryKey: ['kids-church', 'open-checkins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kids_church_checkins')
        .select('id, child_id, checked_in_by, check_in_time')
        .is('check_out_time', null);
      if (error) throw error;
      return data as OpenCheckin[];
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

  React.useEffect(() => {
    if (!hasLoadedOpenCheckinsRef.current) {
      hasLoadedOpenCheckinsRef.current = true;
      openCheckinsRef.current = openCheckins;
      return;
    }

    const prev = openCheckinsRef.current ?? [];
    const prevIds = new Set(prev.map((item: OpenCheckin) => item.id));
    const nextIds = new Set(openCheckins.map((item: OpenCheckin) => item.id));

    const added = openCheckins.filter((item: OpenCheckin) => !prevIds.has(item.id));
    const removed = prev.filter((item: OpenCheckin) => !nextIds.has(item.id));

    if (added.length || removed.length) {
      console.debug('[KidsChurch][polling] diff', {
        added: added.map((item: OpenCheckin) => item.id),
        removed: removed.map((item: OpenCheckin) => item.id),
      });
    }

    added.forEach((item: OpenCheckin) => showCheckinToast(item));
    removed.forEach((item: OpenCheckin) => {
      void showCheckoutToastById(item.id);
    });

    openCheckinsRef.current = openCheckins;
  }, [openCheckins, showCheckinToast, showCheckoutToastById]);

  const currentCheckinsCount = React.useMemo(
    () => new Set((openCheckins ?? []).map((item) => item.child_id).filter(Boolean)).size,
    [openCheckins]
  );
  const checkinRate =
    typeof totalChildren === 'number' && totalChildren > 0
      ? Math.round((currentCheckinsCount / totalChildren) * 100)
      : null;
  const checkinRateLabel =
    checkinRate !== null ? `Check-in rate ${checkinRate}% of registered kids.` : 'Active attendance for today.';


  const handleAddChild = () => {
    // TODO: navigate to a dedicated form page when implemented
    navigate('/kids-church/new');
  };

  const sendKidsChurchPush = React.useCallback(
    async (payload: {
      action: 'checkin' | 'checkout';
      childId: string;
      childName: string;
      checkinId?: string | null;
      checkInTime?: string | null;
      checkOutTime?: string | null;
    }) => {
      try {
        const actorName =
          (user?.user_metadata as { full_name?: string } | undefined)?.full_name ||
          user?.email ||
          'Staff';
        const title = payload.action === 'checkin' ? 'Kids Church Check-in' : 'Kids Church Check-out';
        const body =
          payload.action === 'checkin'
            ? `${payload.childName} was checked in by ${actorName}.`
            : `${payload.childName} was checked out by ${actorName}.`;

        await supabase.functions.invoke('send-push-notification-enhanced', {
          body: {
            title,
            body,
            category: 'kids_church',
            targetRoles: ['teacher'],
            data: {
              action: payload.action,
              childId: payload.childId,
              childName: payload.childName,
              checkinId: payload.checkinId ?? null,
              checkInTime: payload.checkInTime ?? null,
              checkOutTime: payload.checkOutTime ?? null,
              source: 'web',
            },
          },
        });
      } catch (error) {
        console.warn('[KidsChurch] Failed to send push notification', error);
      }
    },
    [user?.email, user?.user_metadata]
  );

  // Mutation for check-in
  const checkInMutation = useMutation({
    mutationFn: async (payload: { childId: string; childName: string }) => {
      const { data: existingOpen, error: existingError } = await supabase
        .from('kids_church_checkins')
        .select('id, check_in_time, check_out_time')
        .eq('child_id', payload.childId)
        .is('check_out_time', null)
        .order('check_in_time', { ascending: false })
        .limit(1);
      if (existingError) throw existingError;
      if (existingOpen?.length) {
        return existingOpen[0];
      }

      const { data, error } = await supabase
        .from('kids_church_checkins')
        .insert({
          child_id: payload.childId,
          checked_in_by: user?.id ?? null,
        })
        .select('id, check_in_time, check_out_time')
        .single();
      if (error) throw error;

      await sendKidsChurchPush({
        action: 'checkin',
        childId: payload.childId,
        childName: payload.childName,
        checkinId: data?.id,
        checkInTime: data?.check_in_time ?? null,
        checkOutTime: data?.check_out_time ?? null,
      });
      return data;
    },
    onSuccess: () => {
      // Refresh metrics and maybe children list
      queryClient.invalidateQueries({ queryKey: ['kids-church', 'open-checkins'] });
      queryClient.invalidateQueries({ queryKey: ['kids-children'] });
    },
  });

  // Mutation for check-out — close every open check-in for the child
  const checkOutMutation = useMutation({
    mutationFn: async (payload: { childId: string; childName: string }) => {
      const checkedOutAt = new Date().toISOString();
      const { data, error } = await supabase
        .from('kids_church_checkins')
        .update({
          check_out_time: checkedOutAt,
          checked_out_by: user?.id ?? null,
        })
        .eq('child_id', payload.childId)
        .is('check_out_time', null)
        .select('id, child_id, check_in_time, check_out_time');
      if (error) throw error;
      if (!data?.length) {
        throw new Error('No open check-in found for this child');
      }

      const primary = data[0];
      await sendKidsChurchPush({
        action: 'checkout',
        childId: primary.child_id ?? payload.childId,
        childName: payload.childName,
        checkinId: primary.id,
        checkInTime: primary.check_in_time ?? null,
        checkOutTime: primary.check_out_time ?? null,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kids-church', 'open-checkins'] });
      queryClient.invalidateQueries({ queryKey: ['kids-children'] });
    },
  });

  // Toggle handler to check a child in or out
  const handleToggleCheck = (childId: string, childName: string, isCheckedIn: boolean) => {
    const localActorLabel = isStaff ? 'Staff' : 'Parent';
    if (isCheckedIn) {
      lastLocalActionRef.current = { childId, type: 'checkout', at: Date.now() };
      checkOutMutation.mutate({ childId, childName }, {
        onSuccess: () => showToast(`${childName} checked out by ${localActorLabel}`, 'success'),
        onError: () => {
          lastLocalActionRef.current = null;
          showToast(`Check out failed for ${childName}`, 'error');
        },
      });
    } else {
      lastLocalActionRef.current = { childId, type: 'checkin', at: Date.now() };
      checkInMutation.mutate({ childId, childName }, {
        onSuccess: () => showToast(`${childName} checked in by ${localActorLabel}`, 'success'),
        onError: () => {
          lastLocalActionRef.current = null;
          showToast(`Check in failed for ${childName}`, 'error');
        },
      });
    }
  };

  const handleMessageParent = (parentId?: string) => {
    if (!parentId) {
      // eslint-disable-next-line no-alert
      alert('No parent information available');
      return;
    }
    navigate(`/kids-church/messages?recipient=${parentId}`);
  };

  const addToHouseholdMutation = useMutation({
    mutationFn: async (payload: { childId: string; householdId: string }) => {
      const { error } = await supabase.functions.invoke('create-child-account', {
        body: {
          existingChildId: payload.childId,
          householdId: payload.householdId,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      showToast('Child added to household', 'success');
      setHouseholdModalChild(null);
      setHouseholdSearch('');
      setSelectedHouseholdId(null);
      queryClient.invalidateQueries({ queryKey: ['kids-children'] });
    },
    onError: () => {
      showToast('Failed to add child to household', 'error');
    },
  });

  const createWallPost = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('You must be signed in to post.');
      }
      const trimmedTitle = wallTitle.trim();
      const trimmedContent = wallContent.trim();
      const trimmedImage = wallImageUrl.trim();
      if (!trimmedContent) {
        throw new Error('Post content is required.');
      }
      const payload: Record<string, any> = {
        author_id: user.id,
        content: trimmedContent,
        status: 'published',
        type: 'kids_church',
      };
      if (trimmedTitle) payload.title = trimmedTitle;
      if (trimmedImage) payload.images = [trimmedImage];
      const { error } = await supabase.from('wall_posts').insert([payload]);
      if (error) throw error;
    },
    onSuccess: () => {
      setWallTitle('');
      setWallContent('');
      setWallImageUrl('');
      if (wallImageInputRef.current) wallImageInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const uploadToStorage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'bin';
    const fileName = `posts/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, {
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleWallImageUpload = async (file?: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert('Please upload an image smaller than 5MB.');
      return;
    }
    setUploadingWallImage(true);
    try {
      const url = await uploadToStorage(file);
      setWallImageUrl(url);
    } catch (err) {
      console.error(err);
      window.alert('Image upload failed. Please try again.');
    } finally {
      setUploadingWallImage(false);
      if (wallImageInputRef.current) wallImageInputRef.current.value = '';
    }
  };

  const totalUnreadCount = React.useMemo(
    () => chatUsers.reduce((sum, chatUser) => sum + (chatUser.unreadCount ?? 0), 0),
    [chatUsers]
  );
  const totalPages = React.useMemo(
    () => Math.max(1, Math.ceil(totalKidsCount / pageSize)),
    [pageSize, totalKidsCount]
  );

  const skipInitialSearchReset = React.useRef(true);
  React.useEffect(() => {
    if (skipInitialSearchReset.current) {
      skipInitialSearchReset.current = false;
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [showCheckedInOnly, classFilter]);

  React.useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageIndicators = React.useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const indicators: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    if (start > 2) {
      indicators.push('ellipsis');
    }

    for (let page = start; page <= end; page += 1) {
      indicators.push(page);
    }

    if (end < totalPages - 1) {
      indicators.push('ellipsis');
    }

    indicators.push(totalPages);
    return indicators;
  }, [currentPage, totalPages]);

  if (kidsLoading && !kidsResult) return <p>Loading kids…</p>;
  if (kidsError && !kidsResult) {
    return (
      <div>
        <p>Error loading kids</p>
        <pre style={{ whiteSpace: 'pre-wrap', color: 'var(--error)' }}>
          {kidsError instanceof Error ? kidsError.message : JSON.stringify(kidsError)}
        </pre>
      </div>
    );
  }

  const selectedKid = selectedKidId
    ? kids.find((k) => k.id === selectedKidId) ?? null
    : null;

  const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

  return (
    <Page>
      <PageHeader>
        <TitleGroup>
          <TitleIcon>K</TitleIcon>
          <TitleText>
            <Title>Kids Church</Title>
            <Subtitle>Overview Dashboard</Subtitle>
          </TitleText>
        </TitleGroup>
        <HeaderActions>
         
          <HeaderButton $variant="primary" onClick={handleAddChild}>
            + New Registration
          </HeaderButton>
        </HeaderActions>
      </PageHeader>

      {toast ? (
        <ToastRegion role="status" aria-live="polite">
          <Toast $variant={toast.variant}>
            <div>{toast.message}</div>
            <ToastTimestamp>{toast.timestamp}</ToastTimestamp>
          </Toast>
        </ToastRegion>
      ) : null}

      <TabsBar>
        <TabButton $active={activeTab === 'children'} onClick={() => setActiveTab('children')}>
          <TabIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M3 7H9V21H3V7Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M11 3H21V21H11V3Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </TabIcon>
          Children
        </TabButton>
        <TabButton $active={activeTab === 'wall'} onClick={() => setActiveTab('wall')}>
          <TabIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 6H20V18H4V6Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M8 10H16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M8 14H13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </TabIcon>
          Wall Posts
        </TabButton>
        <TabButton $active={activeTab === 'messages'} onClick={() => setActiveTab('messages')}>
          <TabIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 5C4 3.89543 4.89543 3 6 3H18C19.1046 3 20 3.89543 20 5V15C20 16.1046 19.1046 17 18 17H9L4 21V5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </TabIcon>
          Messages
          {totalUnreadCount > 0 ? (
            <MessageCountPill>{totalUnreadCount}</MessageCountPill>
          ) : null}
        </TabButton>
      </TabsBar>

      <MetricsGrid>
        <Card>
          <MetricTop>
            <MetricIcon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M7 11C9.20914 11 11 9.20914 11 7C11 4.79086 9.20914 3 7 3C4.79086 3 3 4.79086 3 7C3 9.20914 4.79086 11 7 11Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M17 13C19.2091 13 21 11.2091 21 9C21 6.79086 19.2091 5 17 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M3 21C3 17.6863 5.68629 15 9 15H10C13.3137 15 16 17.6863 16 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M18 21C18 18.7909 16.6569 16.8946 14.75 16.05"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </MetricIcon>
          </MetricTop>
          <MetricLabel>Total Registered Children</MetricLabel>
          <MetricValue>{totalChildren ?? '…'}</MetricValue>
          <MetricFootnote>Registered kids in the system.</MetricFootnote>
        </Card>

        <Card>
          <MetricTop>
            <MetricIcon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M4 12L9 17L20 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MetricIcon>
            <LiveBadge>LIVE NOW</LiveBadge>
          </MetricTop>
          <MetricLabel>Currently Checked In</MetricLabel>
          <MetricValue>{openCheckins ? currentCheckinsCount : '…'}</MetricValue>
          <MetricFootnote>{checkinRateLabel}</MetricFootnote>
        </Card>
      </MetricsGrid>

      {activeTab === 'children' ? (
        <>
          <SectionHeader>
            <div>
              <SectionTitle>Children Management</SectionTitle>
              <SectionSubtitle>
                {kidsFetching
                  ? 'Updating results…'
                  : "Manage attendance and child details for today's session."}
              </SectionSubtitle>
            </div>
            <SectionActions>
              <SearchBox>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M21 21L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <SearchInput
                  type="search"
                  placeholder="Search children..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                />
              </SearchBox>
              <ToggleButton $active={showCheckedInOnly} onClick={() => setShowCheckedInOnly((prev) => !prev)}>
                Checked-in only
              </ToggleButton>
              <ClassFilter
                aria-label="Filter by class"
                value={classFilter}
                onChange={(event) => setClassFilter(event.target.value as KidsChurchClassId | '')}
              >
                <option value="">All classes</option>
                {KIDS_CHURCH_CLASSES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </ClassFilter>
            </SectionActions>
          </SectionHeader>
          <TableCard>
            <Table>
              <thead>
                <tr>
                  <th>Child&apos;s Name</th>
                  <th>Class</th>
                  {canViewParentPins ? <th>Parent PIN</th> : null}
                  <th>Status</th>
                  <th>Allergies/Medical</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {kids.length === 0 ? (
                  <tr>
                    <td colSpan={childColumnCount} style={{ padding: '1.5rem 1rem', color: 'var(--onSurfaceVariant)' }}>
                      No children found.
                    </td>
                  </tr>
                ) : (
                  kids.map((k) => {
                    const checkinRecord = openCheckins?.find((c) => c.child_id === k.id);
                    const isCheckedIn = Boolean(checkinRecord);
                    const childName =
                      `${k.first_name ?? ''} ${k.last_name ?? ''}`.trim() || k.parent_name || 'Child';
                    const tagItems = [
                      k.allergies ? { label: k.allergies, tone: 'danger' as const } : null,
                      k.medical_conditions
                        ? { label: k.medical_conditions, tone: 'warning' as const }
                        : null,
                      k.special_needs ? { label: k.special_needs, tone: 'neutral' as const } : null,
                    ].filter(
                      (tag): tag is { label: string; tone: 'danger' | 'warning' | 'neutral' } => Boolean(tag)
                    );
                    return (
                      <tr
                        key={k.id}
                        onClick={() => setSelectedKidId(k.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <NameCell>
                            <Avatar>
                              {(k.first_name?.[0] ?? k.last_name?.[0] ?? 'K').toUpperCase()}
                            </Avatar>
                            <div>
                              <div>
                                {k.first_name} {k.last_name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--onSurfaceVariant)' }}>
                                {k.parent_name ?? 'No parent assigned'}
                              </div>
                            </div>
                          </NameCell>
                        </td>
                        <td>{getKidsChurchClass(k.date_of_birth)}</td>
                        {canViewParentPins ? <td>{formatParentPin(k.parent_pin)}</td> : null}
                        <td>
                          <StatusPill $status={isCheckedIn ? 'in' : 'out'}>
                            {isCheckedIn ? 'In' : 'Out'}
                          </StatusPill>
                        </td>
                        <td>
                          {tagItems.length ? (
                            <TagsRow>
                              {tagItems.map((tag) => (
                                <Tag key={tag.label} $tone={tag.tone}>
                                  {tag.label}
                                </Tag>
                              ))}
                            </TagsRow>
                          ) : (
                            <span style={{ color: 'var(--onSurfaceVariant)' }}>None</span>
                          )}
                        </td>
                        <td>
                          <RowActions>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMessageParent(k.parent_id ?? undefined);
                              }}
                              disabled={!k.parent_id}
                              aria-label="Message parent"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 5C4 3.89543 4.89543 3 6 3H18C19.1046 3 20 3.89543 20 5V15C20 16.1046 19.1046 17 18 17H9L4 21V5Z"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </IconButton>
                            {isStaff && !k.household_id ? (
                              <RowActionButton
                                $variant="secondary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setHouseholdModalChild({
                                    id: k.id,
                                    name: childName,
                                  });
                                  setHouseholdSearch('');
                                  setSelectedHouseholdId(null);
                                }}
                              >
                                Add to household
                              </RowActionButton>
                            ) : null}
                            <RowActionButton
                              $variant={isCheckedIn ? 'danger' : 'primary'}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleCheck(k.id, childName, isCheckedIn);
                              }}
                              disabled={checkInMutation.isPending || checkOutMutation.isPending}
                            >
                              {isCheckedIn ? 'Check Out' : 'Check In'}
                            </RowActionButton>
                          </RowActions>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <TableFooter>
              <span>
                {totalKidsCount === 0
                  ? `Showing 0 of 0 ${showCheckedInOnly ? 'checked-in' : 'registered'} children`
                  : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                      currentPage * pageSize,
                      totalKidsCount
                    )} of ${totalKidsCount} ${showCheckedInOnly ? 'checked-in' : 'registered'} children`}
              </span>
              {totalPages > 1 && (
                <Pagination>
                  <PageButton
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    {'<'}
                  </PageButton>
                  {pageIndicators.map((indicator, index) =>
                    indicator === 'ellipsis' ? (
                      <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                    ) : (
                      <PageButton
                        key={indicator}
                        type="button"
                        $active={indicator === currentPage}
                        onClick={() => setCurrentPage(indicator)}
                        aria-label={`Page ${indicator}`}
                      >
                        {indicator}
                      </PageButton>
                    )
                  )}
                  <PageButton
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    {'>'}
                  </PageButton>
                </Pagination>
              )}
            </TableFooter>
          </TableCard>
        </>
      ) : activeTab === 'wall' ? (
        <>
          <h2>Wall Posts</h2>
          {isStaff ? (
            <WallFormCard>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createWallPost.mutate();
                }}
              >
                <FormGrid>
                  <FormRowFull>
                    <label htmlFor="kids-wall-title">Title (optional)</label>
                    <Input
                      id="kids-wall-title"
                      value={wallTitle}
                      onChange={(e) => setWallTitle(e.target.value)}
                      placeholder="Post title"
                    />
                  </FormRowFull>
                  <FormRowFull>
                    <label htmlFor="kids-wall-content">Content</label>
                    <TextArea
                      id="kids-wall-content"
                      value={wallContent}
                      onChange={(e) => setWallContent(e.target.value)}
                      placeholder="Share an update with kids church..."
                      required
                    />
                  </FormRowFull>
                  <FormRowFull>
                    <label htmlFor="kids-wall-image">Image (optional)</label>
                    <Input
                      ref={wallImageInputRef}
                      id="kids-wall-image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleWallImageUpload(e.target.files?.[0])}
                      disabled={uploadingWallImage || createWallPost.isPending}
                    />
                    {wallImageUrl ? (
                      <WallImagePreview src={wallImageUrl} alt="Wall post preview" />
                    ) : null}
                    {uploadingWallImage ? <span>Uploading…</span> : null}
                  </FormRowFull>
                </FormGrid>
                {createWallPost.isError && (
                  <ErrorText>
                    {createWallPost.error instanceof Error
                      ? createWallPost.error.message
                      : 'Failed to post.'}
                  </ErrorText>
                )}
                <ActionButton type="submit" disabled={createWallPost.isPending || uploadingWallImage}>
                  {createWallPost.isPending ? 'Posting...' : 'Post'}
                </ActionButton>
              </form>
            </WallFormCard>
          ) : (
            <p>Only Kids Church staff can create wall posts.</p>
          )}

          {wallLoading ? (
            <p>Loading wall posts…</p>
          ) : wallError ? (
            <p>Error loading wall posts</p>
          ) : wallPosts.length === 0 ? (
            <p>No wall posts yet.</p>
          ) : (
            <WallPostsGrid>
              {wallPosts.map((post) => (
                <WallPostCard key={post.id}>
                  <WallMeta>
                    {(post.author_name ?? post.author_id) as string} •{' '}
                    {new Date(post.created_at).toLocaleString()}
                  </WallMeta>
                  {post.title ? <WallTitle>{post.title}</WallTitle> : null}
                  <WallBody>{post.content}</WallBody>
                  {Array.isArray(post.images) && post.images.length > 0 ? (
                    <WallPostImage src={post.images[0]} alt="Wall post" />
                  ) : null}
                </WallPostCard>
              ))}
            </WallPostsGrid>
          )}
        </>
      ) : (
        <>
          <h2>Messages</h2>
          <p>Open the Kids Church messaging inbox to chat with parents and staff.</p>
          <ActionButton $variant="secondary" onClick={() => navigate('/kids-church/messages')}>
            Open Messages
          </ActionButton>
        </>
      )}

      {selectedKid ? (
        <ModalOverlay
          onClick={() => setSelectedKidId(null)}
          role="presentation"
          aria-hidden="true"
        >
          <ModalContent
            role="dialog"
            aria-modal="true"
            aria-labelledby="kid-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle id="kid-modal-title">
                {selectedKid.first_name} {selectedKid.last_name}
              </ModalTitle>
              <ModalCloseButton onClick={() => setSelectedKidId(null)} aria-label="Close">
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalGrid>
              <div>
                <ModalLabel>Date of Birth</ModalLabel>
                <ModalValue>
                  {selectedKid.date_of_birth
                    ? new Date(selectedKid.date_of_birth).toLocaleDateString()
                    : '—'}
                </ModalValue>
              </div>
              <div>
                <ModalLabel>Class</ModalLabel>
                <ModalValue>{getKidsChurchClass(selectedKid.date_of_birth)}</ModalValue>
              </div>
              <div>
                <ModalLabel>Parent</ModalLabel>
                <ModalValue>{selectedKid.parent_name ?? '—'}</ModalValue>
              </div>
              {canViewParentPins ? (
                <div>
                  <ModalLabel>Parent PIN</ModalLabel>
                  <ModalValue>{formatParentPin(selectedKid.parent_pin)}</ModalValue>
                </div>
              ) : null}
              <div>
                <ModalLabel>Emergency Contact</ModalLabel>
                <ModalValue>{selectedKid.emergency_contact_name ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Emergency Phone</ModalLabel>
                <ModalValue>{selectedKid.emergency_contact_phone ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Relationship</ModalLabel>
                <ModalValue>{selectedKid.emergency_contact_relationship ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Allergies</ModalLabel>
                <ModalValue>{selectedKid.allergies ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Medical</ModalLabel>
                <ModalValue>{selectedKid.medical_conditions ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Special Needs</ModalLabel>
                <ModalValue>{selectedKid.special_needs ?? '—'}</ModalValue>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <ModalLabel>Notes for Teacher</ModalLabel>
                <ModalValue>{selectedKid.teacher_notes ?? '—'}</ModalValue>
              </div>
              <div>
                <ModalLabel>Last Check-In</ModalLabel>
                <ModalValue>{formatDateTime(lastCheckin?.check_in_time)}</ModalValue>
              </div>
              <div>
                <ModalLabel>Last Check-Out</ModalLabel>
                <ModalValue>
                  {lastCheckin?.check_out_time
                    ? formatDateTime(lastCheckin.check_out_time)
                    : lastCheckin?.check_in_time
                    ? 'Still checked in'
                    : '—'}
                </ModalValue>
              </div>
              <div>
                <ModalLabel>Checked In By</ModalLabel>
                <ModalValue>
                  {getActorLabel(lastCheckin?.checked_in_by, selectedKid.parent_id, '—')}
                </ModalValue>
              </div>
              <div>
                <ModalLabel>Checked Out By</ModalLabel>
                <ModalValue>
                  {lastCheckin?.check_out_time
                    ? getActorLabel(lastCheckin?.checked_out_by, selectedKid.parent_id, '—')
                    : '—'}
                </ModalValue>
              </div>
            </ModalGrid>
          </ModalContent>
        </ModalOverlay>
      ) : null}
      {householdModalChild ? (
        <ModalOverlay onClick={() => setHouseholdModalChild(null)} role="presentation" aria-hidden="true">
          <ModalContent
            role="dialog"
            aria-modal="true"
            aria-labelledby="household-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle id="household-modal-title">Add to household</ModalTitle>
              <ModalCloseButton onClick={() => setHouseholdModalChild(null)} aria-label="Close">
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalField>
              <ModalLabel>Child</ModalLabel>
              <ModalValue>{householdModalChild.name}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Household</ModalLabel>
              <Input
                value={householdSearch}
                onChange={(event) => {
                  setHouseholdSearch(event.target.value);
                  setSelectedHouseholdId(null);
                }}
                placeholder="Search households..."
              />
              {householdSearch.trim().length > 0 && filteredHouseholds.length > 0 ? (
                <DropdownList>
                  {filteredHouseholds.map((household) => (
                    <DropdownItem
                      key={household.id}
                      onClick={() => {
                        setSelectedHouseholdId(household.id);
                        setHouseholdSearch(household.name);
                      }}
                    >
                      {household.name}
                    </DropdownItem>
                  ))}
                </DropdownList>
              ) : null}
            </ModalField>
            <SectionActions style={{ marginTop: '1rem' }}>
              <ActionButton
                type="button"
                onClick={() => {
                  if (!selectedHouseholdId) return;
                  addToHouseholdMutation.mutate({
                    childId: householdModalChild.id,
                    householdId: selectedHouseholdId,
                  });
                }}
                disabled={!selectedHouseholdId || addToHouseholdMutation.isPending}
              >
                {addToHouseholdMutation.isPending ? 'Adding...' : 'Add to household'}
              </ActionButton>
              <ActionButton
                type="button"
                $variant="secondary"
                onClick={() => setHouseholdModalChild(null)}
                disabled={addToHouseholdMutation.isPending}
              >
                Cancel
              </ActionButton>
            </SectionActions>
          </ModalContent>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default KidsChurchPage;
